import React from 'react';
import { useTranslation } from 'react-i18next';

/* Material UI */
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import Chip from '@material-ui/core/Chip';

/* Project */
import { truthty, falsy } from 'utils/functions';
import ProcessEquipments from '../ProcessEquipments';

function Headers(props) {
  const { t } = useTranslation();
  const {
    classes,
    process,
    multipleLotsProcess,
    uniqueLot,
    toggleClose,
    changeControls,
    controls,
    classes: { closeProcessButton },
    isAdmin,
    processEquipments,
    equipments,
    toggleDelete,
    onBackAction,
    hasBalanceError,
    role,
  } = props;

  function formatDateTime(date) {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    };
    return new Intl.DateTimeFormat('es-ES', options).format(date);
  }

  if (!truthty(process)) return null;

  const processCategory =
    (typeof process?.processCategories === 'function' &&
      process.processCategories()) ||
    process?.ProcessCategory ||
    process?.processCategory ||
    null;
  const processType =
    (typeof process?.processType === 'function' && process.processType()) ||
    process?.ProcessType ||
    process?.processType ||
    null;
  const workShiftType =
    (typeof process?.workShiftType === 'function' && process.workShiftType()) ||
    process?.WorkShiftType ||
    process?.workShiftType ||
    null;
  const lotNumbersFromUniqueLot =
    typeof uniqueLot?.lotNumbers === 'function' ? uniqueLot.lotNumbers() : [];
  const processModules = Array.isArray(process?.modules) ? process.modules : [];
  const lotNumberLabel =
    (Array.isArray(lotNumbersFromUniqueLot) && lotNumbersFromUniqueLot.length
      ? lotNumbersFromUniqueLot.join(', ')
      : null) ||
    process?.lotNumber ||
    process?.admitedLotNumber ||
    '-';

  return (
    <Grid item container spacing={2}>
      <Grid container item>
        <Grid item xs={12}>
          <Button
            startIcon={<ArrowBackIosIcon />}
            style={{ marginTop: 20 }}
            variant="outlined"
            color="primary"
            onClick={onBackAction}>
            {t('process.back')}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5" style={{ color: '#47AA4D' }} className={classes.marginTop}>
            Nº
            {process.id} {processCategory ? ` - ${processCategory.name}` : ''}
          </Typography>
          <Grid item xs={12} />
          <Typography variant="h3" component="h3">
            {t('process.processOf')} {processType?.name?.toLowerCase() || ''}
            {!multipleLotsProcess && (
              <Tooltip title={t('process.restrictedToLot')}>
                <InfoOutlinedIcon style={{ marginLeft: 8 }} />
              </Tooltip>
            )}
          </Typography>
        </Grid>
        <Grid item xs={12} md={10}>
          <Typography variant="h5" component="span" className={classes.marginTop}>
            <span style={{ fontWeight: 'bold' }}>{t('process.shift')}:</span>
            {` ${workShiftType?.name?.toLowerCase() || ''}`}
            <span style={{ fontWeight: 'bold' }}> | {t('process.startDate')}:</span>
            {` ${truthty(process) && formatDateTime(new Date(process.date))}`}
            {(truthty(uniqueLot) || truthty(process?.lotNumber) || truthty(process?.admitedLotNumber)) && (
              <span>
                <span style={{ fontWeight: 'bold' }}> | {t('process.lotNumber')}:</span> {lotNumberLabel}
              </span>
            )}
            {truthty(process.closedAt) && (
              <span>
                <span style={{ fontWeight: 'bold' }}> | {t('process.endDate')}:</span>{' '}
                {formatDateTime(new Date(process.closedAt))}
              </span>
            )}
          </Typography>
        </Grid>
        {!!processModules.length && (
          <Grid spacing={3} container style={{ marginTop: '1px' }}>
            <Grid
              item
              xs={12}
              md={6}
              style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <Typography variant="h5" component="span">
                <span style={{ fontWeight: 'bold' }}>
                  {t('process.modules')} ({processModules.length}):
                </span>{' '}
              </Typography>
              {processModules.map((module) => (
                <Tooltip title={module.name} key={module.id}>
                  <Chip
                    label={
                      <Typography noWrap className={classes.chipLabel}>
                        {module.name}
                      </Typography>
                    }
                    className={classes.chipRoot}
                  />
                </Tooltip>
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid item container justifyContent="flex-start" spacing={3}>
        <Grid container justifyContent="flex-start" item xs={12} sm={6}>
          <Grid container item xs={12} sm={6}>
            <ProcessEquipments
              classes={classes}
              process={process}
              processEquipments={processEquipments}
              equipments={equipments}
              toggleDelete={toggleDelete}
            />
          </Grid>
        </Grid>
        <Grid container justifyContent="flex-end" item xs={12} sm={6}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isGroupedInfo"
                  checked={controls.isGroupedInfo}
                  onChange={changeControls}
                  color="primary"
                  inputProps={{ 'data-module': 'headers' }}
                />
              }
              label={t('process.groupInformation')}
            />
          </Grid>
          {(isAdmin || falsy(process.closedAt)) && (
            <Grid item xs={12} sm={6} md={4}>
              <Button
                className={falsy(process.closedAt) && closeProcessButton}
                fullWidth
                variant="contained"
                disabled={hasBalanceError && role !== 1}
                color={falsy(process.closedAt) ? 'default' : 'primary'}
                onClick={toggleClose}>
                {truthty(process.closedAt)
                  ? t('process.openProcessAction')
                  : t('process.finishProcessAction')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

export default Headers;
